// Collapsible wrapper for the Citations Preview panel with a top drag handle
// to resize the panel upward (more vertical room for cramped citations UX).
// Height is persisted per-workspace in localStorage.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronRight, GripHorizontal } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

type Props = {
  workspaceId: string;
  children: ReactNode;
  /** When this flips to true the panel opens itself (user can still close it). */
  hasCitations?: boolean;
};

const DEFAULT_HEIGHT = 420;
const MIN_HEIGHT = 240;
const storageKey = (wsId: string) => `composer:citationsPreviewHeight:${wsId}`;

export const CitationsPreviewCollapsible = ({ workspaceId, children, hasCitations = false }: Props) => {
  const [open, setOpen] = useState<boolean>(hasCitations);
  const prevHas = useRef(hasCitations);
  useEffect(() => {
    if (hasCitations && !prevHas.current) setOpen(true);
    prevHas.current = hasCitations;
  }, [hasCitations]);
  const [height, setHeight] = useState<number>(DEFAULT_HEIGHT);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const maxHeight = useRef<number>(900);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey(workspaceId));
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed) && parsed >= MIN_HEIGHT) setHeight(parsed);
    } catch {
      // ignore
    }
  }, [workspaceId]);

  useEffect(() => {
    const update = () => {
      maxHeight.current = Math.min(window.innerHeight * 0.8, 1100);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = { startY: e.clientY, startH: height };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [height]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = drag.startY - e.clientY; // drag up = positive
    const next = Math.min(maxHeight.current, Math.max(MIN_HEIGHT, drag.startH + delta));
    setHeight(next);
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    try {
      window.localStorage.setItem(storageKey(workspaceId), String(height));
    } catch {
      // ignore
    }
  }, [workspaceId, height]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border bg-card">
        <CollapsibleTrigger className="group flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-muted/40">
          <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
          Citations Preview
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-border">
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize Citations Preview"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className="flex h-3 cursor-ns-resize select-none items-center justify-center border-b border-border bg-muted/20 hover:bg-muted/40"
          >
            <GripHorizontal className="h-3 w-3 text-muted-foreground" />
          </div>
          <div
            className="overflow-auto px-4 py-4"
            style={{ height: `${height}px` }}
          >
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
